import { useEffect, useState, useCallback } from 'react';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EditOutlined,
  FormOutlined,
  SendOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { AModal, ASelect, ATextArea } from '../../../components';
import {
  Col,
  Form,
  Row,
  DatePicker,
  TimePicker,
  Button,
  Card,
  Typography,
} from 'antd';
import {
  IClient,
  IScheduleStatus,
  ISchedule,
  IProfessional,
} from '../../../shared/interfaces';
import { DateClickArg } from '@fullcalendar/interaction';
import { schemaSchedule } from '../schemas/yup.schedule.schema';
import { useToast } from '../../../hooks/toast';
import { patchSchedule, postSchedule } from '../../../http';
import dayjs from 'dayjs';
import SendConfirmationModal from '../../../components/SendConfirmationModal';

interface IEventProps {
  open: boolean;
  close: () => void;
  clients: IClient[];
  professionals: IProfessional[];
  schedulesStatus: IScheduleStatus[];
  event: ISchedule | null;
  setEvents: React.Dispatch<React.SetStateAction<ISchedule[]>>;
  arg: DateClickArg | null;
}

const CreateEditEvent: React.FC<IEventProps> = ({
  open,
  close,
  clients,
  professionals,
  schedulesStatus,
  event,
  setEvents,
  arg,
}) => {
  const { addToast } = useToast();
  const [form] = Form.useForm();

  const [date, setDate] = useState<string | null>(null);
  const [client_id, setClient_id] = useState<number>();
  const [professional_id, setProfessiona_id] = useState<string>();
  const [time_start, setTime_start] = useState<string>();
  const [time_end, setTime_end] = useState<string>();
  const [observation, setObservation] = useState<string>();
  const [color, setColor] = useState<string>();
  const [sendConfirmationOpen, setSendConfirmationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { Text } = Typography;

  const resetForm = useCallback(() => {
    setDate(null);
    setClient_id(undefined);
    setProfessiona_id(undefined);
    setTime_start(undefined);
    setTime_end(undefined);
    setObservation(undefined);
    setColor(undefined);
    form.resetFields();
  }, [form]);

  const handleCreate = useCallback(async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const foundClient = clients.find((x) => Number(x.code) === Number(client_id));
      const title =
        foundClient?.corporate_name ||
        observation ||
        'Agendamento';
      const finalStart = `${date} ${time_start}`;
      const finalEnd = `${date} ${time_end}`;
      const defaultColor =
        schedulesStatus?.[0]?.color ||
        color ||
        '#1f725f';
      const validated = await schemaSchedule.validate({
        title,
        color: defaultColor,
        start: finalStart,
        end: finalEnd,
        client_id: client_id !== undefined ? String(client_id) : undefined,
        professional_id,
        observation,
      });
      const data = await postSchedule(validated);
      setEvents((state) => [...state, data]);
      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Agendamento criado com sucesso.',
      });
      resetForm();
      close();
    } catch (err: any) {
      if (err?.errors?.length) {
        addToast({
          type: 'warning',
          title: 'Atenção',
          description: err.errors?.[0] || 'Verifique os campos obrigatórios.',
        });
      } else {
        addToast({
          type: 'error',
          title: 'Erro',
          description: err.response?.data?.error ?? 'Erro ao cadastrar.',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    date,
    time_start,
    time_end,
    client_id,
    professional_id,
    observation,
    color,
    clients,
    schedulesStatus,
    setEvents,
    addToast,
    close,
    resetForm,
  ]);

  const handleEdit = useCallback(async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const baseDate = date || event?.start.split('T')[0];
      const foundClient = clients.find((x) => Number(x.code) === Number(client_id));
      const title =
        foundClient?.corporate_name ||
        event?.title ||
        observation ||
        'Agendamento';
      const finalColor =
        color || schedulesStatus?.[0]?.color || '#1f725f';
      const validated = await schemaSchedule.validate({
        title,
        color: finalColor,
        start: `${baseDate} ${time_start}`,
        end: `${baseDate} ${time_end}`,
        client_id: client_id !== undefined ? String(client_id) : undefined,
        professional_id,
        observation,
      });
      if (!event?.id) return;
      const data = await patchSchedule(event.id, validated);
      setEvents((state) =>
        state.map((e) => (e.id !== data.id ? e : data)),
      );
      addToast({
        type: 'success',
        title: 'Atualizado',
        description: 'Agendamento alterado com sucesso.',
      });
      resetForm();
      close();
    } catch (err: any) {
      if (err?.errors?.length) {
        addToast({
          type: 'warning',
          title: 'Atenção',
          description: err.errors?.[0] || 'Verifique os campos obrigatórios.',
        });
      } else {
        addToast({
          type: 'error',
          title: 'Erro',
          description: err.response?.data?.message ?? 'Erro ao editar.',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    event,
    date,
    color,
    time_start,
    time_end,
    client_id,
    professional_id,
    observation,
    clients,
    schedulesStatus,
    setEvents,
    addToast,
    close,
    resetForm,
  ]);

  useEffect(() => {
    if (open) {
      if (event) {
        const eventDate = event.start.split('T')[0];
        const tStart = event.start.split('T')[1]?.slice(0, 5);
        const tEnd = event.end.split('T')[1]?.slice(0, 5);

        setDate(eventDate);
        setClient_id(event.client_id ? +event.client_id : undefined);
        setProfessiona_id(event.professional_id);
        setTime_start(tStart);
        setTime_end(tEnd);
        setObservation(event.observation);
        setColor(event.color);
      } else if (arg) {
        setDate(arg.dateStr);
        setTime_start(undefined);
        setTime_end(undefined);
        setClient_id(undefined);
        setProfessiona_id(undefined);
        setObservation('');
        setColor(undefined);
      }
    }
  }, [open, event, arg]);

  const getConfirmationStatusLabel = (status?: string) => {
    if (status === 'confirmed') return 'Confirmado';
    if (status === 'cancelled') return 'Cancelado';
    return 'Pendente';
  };

  const getConfirmationStatusColor = (status?: string) => {
    if (status === 'confirmed') return '#389e0d';
    if (status === 'cancelled') return '#ff4d4f';
    return '#faad14';
  };

  return (
    <>
      <AModal
        open={open}
        close={isSubmitting ? undefined : close}
        title={
          event ? (
            <span>
              <EditOutlined /> Editar Agendamento
            </span>
          ) : (
            <span>
              <FormOutlined /> Novo Agendamento
            </span>
          )
        }
        onOk={event ? handleEdit : handleCreate}
        width={600}
        confirmLoading={isSubmitting}
        closable={!isSubmitting}
        maskClosable={!isSubmitting}
        okButtonProps={{ disabled: isSubmitting }}
        cancelButtonProps={{ disabled: isSubmitting }}
      >
        <Form layout="vertical" form={form} style={{ marginTop: 20 }}>
          {/* Seção: Quando */}
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 12,
                paddingBottom: 8,
                borderBottom: '1px solid rgba(31, 114, 95, 0.12)',
              }}
            >
              <CalendarOutlined style={{ color: '#1f725f', fontSize: 13 }} />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#1f725f',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Quando
              </span>
            </div>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Data" required style={{ marginBottom: 0 }}>
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    value={date ? dayjs(date) : null}
                    onChange={(_, dateString) => {
                      const [day, month, year] = (dateString as string).split(
                        '/',
                      );
                      setDate(`${year}-${month}-${day}`);
                    }}
                    allowClear={false}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={
                    <span>
                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                      Início
                    </span>
                  }
                  required
                  style={{ marginBottom: 0 }}
                >
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    minuteStep={15}
                    value={time_start ? dayjs(time_start, 'HH:mm') : null}
                    onChange={(t, timeString) => {
                      const val = Array.isArray(timeString)
                        ? timeString[0]
                        : timeString;
                      setTime_start(val || undefined);
                    }}
                    allowClear={false}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={
                    <span>
                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                      Fim
                    </span>
                  }
                  required
                  style={{ marginBottom: 0 }}
                >
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    minuteStep={15}
                    value={time_end ? dayjs(time_end, 'HH:mm') : null}
                    onChange={(t, timeString) => {
                      const val = Array.isArray(timeString)
                        ? timeString[0]
                        : timeString;
                      setTime_end(val || undefined);
                    }}
                    allowClear={false}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Seção: Quem */}
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 12,
                paddingBottom: 8,
                borderBottom: '1px solid rgba(31, 114, 95, 0.12)',
              }}
            >
              <TeamOutlined style={{ color: '#1f725f', fontSize: 13 }} />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#1f725f',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Quem
              </span>
            </div>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={
                    <span>
                      <UserOutlined style={{ marginRight: 4 }} />
                      Profissional
                    </span>
                  }
                  required
                  style={{ marginBottom: 0 }}
                >
                  <ASelect
                    showSearch
                    placeholder="Selecione..."
                    optionFilterProp="label"
                    onChange={setProfessiona_id}
                    value={professional_id}
                    options={professionals.map((prof) => ({
                      label: prof.name,
                      value: prof.id,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <span>
                      <UserOutlined style={{ marginRight: 4 }} />
                      Cliente/Paciente
                    </span>
                  }
                  required
                  style={{ marginBottom: 0 }}
                >
                  <ASelect
                    showSearch
                    placeholder="Busque pelo nome..."
                    optionFilterProp="label"
                    onChange={setClient_id}
                    value={client_id}
                    options={clients.map((client) => ({
                      label: client.corporate_name,
                      value: client.code,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Status (apenas edição) */}
          {event && (
            <div style={{ marginBottom: 20 }}>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label="Status do Agendamento"
                    style={{ marginBottom: 0 }}
                  >
                    <ASelect
                      placeholder="Selecione o status"
                      onChange={setColor}
                      value={color}
                      options={schedulesStatus.map((status) => ({
                        label: (
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: status.color,
                                flexShrink: 0,
                              }}
                            />
                            {status.description}
                          </span>
                        ),
                        value: status.color,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          )}

          {/* Observações */}
          <div style={{ marginBottom: event ? 16 : 0 }}>
            <Form.Item label="Observações" style={{ marginBottom: 0 }}>
              <ATextArea
                rows={3}
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                placeholder="Detalhes adicionais do atendimento..."
              />
            </Form.Item>
          </div>

          {/* Enviar confirmação + status de confirmação (apenas edição) */}
          {event && (
            <>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={() => setSendConfirmationOpen(true)}
                    style={{ width: '100%' }}
                  >
                    Enviar Confirmação
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <Card
                    size="small"
                    bordered
                    style={{
                      background:
                        event.confirmation_status === 'confirmed'
                          ? '#f6ffed'
                          : '#fafafa',
                      borderLeft: `4px solid ${getConfirmationStatusColor(event.confirmation_status)}`,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <Text strong>Status de Confirmação: </Text>
                        <Text
                          style={{
                            color: getConfirmationStatusColor(
                              event.confirmation_status,
                            ),
                            fontWeight: 600,
                            marginLeft: 6,
                          }}
                        >
                          {getConfirmationStatusLabel(
                            event.confirmation_status,
                          )}
                        </Text>
                      </div>
                      <div style={{ maxWidth: '65%' }}>
                        <Text strong>Nota do Cliente: </Text>
                        <Text ellipsis={{ tooltip: event.confirmation_notes }}>
                          {event.confirmation_notes || '—'}
                        </Text>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </AModal>
      {sendConfirmationOpen && event && (
        <SendConfirmationModal
          open={sendConfirmationOpen}
          onClose={() => setSendConfirmationOpen(false)}
          scheduleId={event.id as string}
          client={clients.find((x) => x.code === event.client_id)}
        />
      )}
    </>
  );
};

export default CreateEditEvent;
